// Consumer-side mirror of the admin editor's home page config
// (ac-main/web: components/Storefront/Editor/homePageConfig.ts). Persisted as a single
// JSON string on IStorefrontSettings.homePageConfig.
//
// IMPORTANT: strictly additive — an absent/empty config yields no inline styles, so the
// Home page renders exactly as it does today until a merchant configures something.
// Style interfaces and builders are reused from searchPageConfig so the live Home page
// matches the admin preview byte-for-byte.

import React from 'react';
import {
    ISearchCardStyle,
    ISearchContainerStyle,
    ISearchElementStyle,
    ISearchPriceStyle,
    ISearchTextStyle,
} from './searchPageConfig';
import { IStorefrontSettings } from '../../Api/Models/StorefrontSettings';

// An iconSvg is a recolorable shape, not a picture: the pod tints it by masking, so only the
// outline matters and the SVG's own colors are ignored. Masking also sizes any SVG correctly
// (mask-size: contain) regardless of the markup's intrinsic width/height. Mirror of the admin
// editor's svgMaskStyle (NavPods/NavPodExample.tsx) — keep the semantics identical or the
// editor preview lies.
export const svgMaskStyle = (svg: string, size: number): React.CSSProperties => {
    const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
    return {
        width: size,
        height: size,
        flex: '0 0 auto',
        backgroundColor: 'currentColor',
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
    };
};

// One attribute (facet) constraint on a nav pod's filter. Emitted as a facet=Key|Value search
// param. Several may be set: same key OR's, different keys AND — the search page's own semantics.
export interface INavPodAttribute {
    key: string;
    value: string;
}

// A nav pod's filter destination. Any subset may be set; the storefront builds a /search URL from
// whichever fields are present (see navPodSearchUrl in HomePage).
export interface INavPodFilter {
    segment?: string;
    category?: string;
    subCategory?: string;
    manufacturer?: string;
    // Product tags. Emitted as repeated tag= params, which the search page OR's.
    tags?: string[];
    attributes?: INavPodAttribute[];
    // Superseded by `attributes` (which holds any number of constraints). Kept so pods authored
    // before that existed keep working; the storefront folds these into the attribute list.
    attributeKey?: string;
    attributeValue?: string;
}

// The flex justify-content for a nav pod alignment. Undefined leaves the stylesheet's default in
// place (centered when wrapping, left-aligned when scrolling), so an unset config changes nothing.
export function navPodJustify(align?: IHomePageConfig['navPodAlign']): string | undefined {
    switch (align) {
        case 'left': return 'flex-start';
        case 'center': return 'center';
        case 'right': return 'flex-end';
        case 'spaceBetween': return 'space-between';
        default: return undefined;
    }
}

// The same alignment for the SCROLLING pod row (navPodScroll), where a plain `center` / `flex-end` is a
// trap: once the pods overflow, centering splits the overflow to BOTH sides and the left half lands in
// negative scroll space the user can never reach — so the first pod is permanently clipped, and
// narrowing the viewport only makes it worse. The `safe` keyword falls back to start alignment exactly
// when the content overflows, so the row still centers while it fits and every pod stays reachable when
// it doesn't. A browser that doesn't understand `safe` drops the declaration and inherits the
// stylesheet's `justify-content: flex-start` for .nav-pod-scroll — also unclipped.
export function navPodScrollJustify(align?: IHomePageConfig['navPodAlign']): string | undefined {
    const justify = navPodJustify(align);
    return justify === 'center' || justify === 'flex-end' ? `safe ${justify}` : justify;
}

// Fold a filter's attribute constraints into one list, tolerating the legacy single-pair form.
// Deduped, because a pod migrated in the admin could carry the same pair in both shapes.
export function navPodAttributes(filter?: INavPodFilter): INavPodAttribute[] {
    const all = [...(filter?.attributes ?? [])];
    if (filter?.attributeKey && filter?.attributeValue) {
        all.push({ key: filter.attributeKey, value: filter.attributeValue });
    }
    const seen = new Set<string>();
    return all.filter((a) => {
        if (!a.key || !a.value) return false;
        const id = `${a.key}|${a.value}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}

// A merchant-authored "nav pod": a large, image-backed shortcut that navigates the shopper
// to a filtered /search view or a URL. Rendered larger (and slightly taller than wide) than the
// automatic category pods.
export interface INavPod {
    id: string;
    name?: string;
    // Secondary call-to-action line under the title (e.g. "Shop now ›"). Styled via navPodLink.
    linkText?: string;
    imageUrl?: string;
    // Inline SVG markup for a recolorable icon (CSS-mask tinted by navPodIconColor / hover color).
    // Takes precedence over imageUrl; the SVG's own colors are ignored (only its shape matters).
    iconSvg?: string;
    filter?: INavPodFilter;
    // When set, the pod navigates here instead of the filtered search (takes precedence over
    // `filter`). http(s) URLs open in a new tab; in-store paths (e.g. /page/services) navigate
    // in place.
    linkUrl?: string;
}

// A single affiliation/award/certification/endorsement logo.
export interface IAffiliationItem {
    id: string;
    imageUrl?: string;
    altText?: string;
    linkUrl?: string;   // optional; when set the logo links here, opening in a new tab
}

// Affiliations / endorsements / awards band. Optional — renders only when items has >=1 entry
// with an imageUrl. Lives inside homePageConfig (no BFF/API change).
export interface IAffiliations {
    items?: IAffiliationItem[];
    label?: string;
    // Optional description/blurb rendered directly under the label (title). Wraps at a readable
    // width and follows the group alignment. Absent = title only (today's behavior).
    description?: string;
    descriptionStyle?: ISearchTextStyle;
    labelPosition?: 'top' | 'left' | 'bottom' | 'none';   // label placement relative to the logo row
    position?: 'top' | 'bottom';                          // home-page placement (default 'bottom')
    align?: 'left' | 'center' | 'right' | 'spaceBetween'; // logo row alignment (default 'center')
    gap?: number;                                         // px gap between logos (default 24)
    // When true, the logos render as a continuous auto-scrolling marquee (looping, pauses on hover)
    // instead of a static wrapped row — the "our technology partners" strip look. Additive: unset =
    // today's static row. `align` is ignored in scroll mode (the track fills the width).
    scroll?: boolean;
    scrollSpeed?: number;                                 // seconds for one full loop (default 30; lower = faster)
    band?: ISearchElementStyle;                           // full-bleed band background
    pod?: ISearchElementStyle;                            // each logo pod
    image?: ISearchElementStyle;                          // the logo <img>
    labelStyle?: ISearchTextStyle;                        // label text style
}

export interface IHomePageConfig {
    // The store title heading shown above the hero banner (the <h1 class="home-title">).
    // Not exposed as a bespoke control — set it via the raw Home Page Style JSON.
    pageTitle?: ISearchTextStyle;
    // The hero banner container — the box that wraps the banner image(s) at the top of
    // the home page (background, border, radius, padding, shadow, etc.).
    banner?: ISearchElementStyle;
    // The banner message text shown under the hero banner (.home-message).
    bannerText?: ISearchTextStyle;
    // The floating "add to cart" button on home product cards (mirrors
    // searchPageConfig.addToCartButton). Set via raw JSON; e.g. { backgroundColor, color }.
    addToCartButton?: ISearchElementStyle;
    // Product cards shown inside home sections.
    card?: ISearchCardStyle;
    title?: ISearchTextStyle;
    price?: ISearchPriceStyle;
    image?: ISearchElementStyle;
    // Category pods: each pod button, its label, and the row/grid that wraps them.
    pod?: ISearchElementStyle;
    podName?: ISearchTextStyle;
    podContainer?: ISearchContainerStyle;
    // Nav pods: merchant-authored image shortcuts to filtered searches, plus their button,
    // label, and container styling (mirrors the category pod style keys above).
    navPods?: INavPod[];
    // The pod button itself. Its width/height set the pod's dimensions (the editor exposes both).
    navPod?: ISearchElementStyle;
    navPodName?: ISearchTextStyle;
    // The pod's image, and its call-to-action line (INavPod.linkText).
    navPodImage?: ISearchElementStyle;
    navPodLink?: ISearchTextStyle;
    navPodIconColor?: string;                             // tint for nav-pod SVG-mask icons (INavPod.iconSvg)
    navPodIconHoverColor?: string;                        // hover recolor for nav-pod SVG-mask icons
    navPodContainer?: ISearchContainerStyle;
    // When true the pod row is a strict horizontal scroller (nowrap + chevrons, styled by
    // scrollButton/scrollbar below) instead of wrapping onto additional lines.
    navPodScroll?: boolean;
    // Horizontal alignment of the pod row. Unset keeps the stylesheet default (centered when
    // wrapping, left when scrolling). 'spaceBetween' spreads the pods across the full width.
    navPodAlign?: 'left' | 'center' | 'right' | 'spaceBetween';
    // Side-scroll sections (IStorefrontSection.scrollProducts): the left/right chevron buttons
    // and the horizontal scrollbar of the product scroller. Set via raw JSON. `scrollButton`
    // styles both arrow buttons (backgroundColor, color, borderColor, borderRadius, …).
    // `scrollbar` is applied to the scrolling row — use standard scrollbar CSS here, e.g.
    // { scrollbarColor: "#ff9900 #000", scrollbarWidth: "thin" }.
    scrollButton?: ISearchElementStyle;
    scrollbar?: ISearchElementStyle;
    // The per-section "more like this / more from this section" link at the bottom-right of a product
    // section. Unset keeps the default faint text link; set backgroundColor/color/borderRadius/padding
    // (etc.) to turn it into a styled button (e.g. a rounded pill). Set opacity:1 to override the
    // link's default 0.6 dimming.
    moreLikeThisButton?: ISearchElementStyle;
    // --- Section collapse/expand control (the +/- on a collapsible section's header) ---
    // Page-wide, mirrored by the admin editor (ac-main Editor/homePageConfig.ts) — keep in step.
    //
    // `sectionToggle` styles the indicator itself: size, color, background, border, radius, and the
    // `right`/`top` offsets that place it on the header band. Unset keeps the shipped look — a 24px
    // glyph inset 20px from the right at 65% opacity, tinted light or dark automatically to contrast
    // with the header band's own color.
    //
    // NOTE: setting `color` opts OUT of that automatic contrast for both header colors, so a store
    // with light AND dark section headers should usually leave color alone.
    sectionToggle?: ISearchElementStyle;
    // Merged over `sectionToggle` while the pointer is anywhere on the section header — emitted as a
    // scoped `.section-header:hover .section-toggle` rule, since the whole band is the toggle.
    //
    // GOTCHA: the indicator is vertically centered with `transform: translateY(-50%)`, so a hover
    // `transform` REPLACES that and the glyph jumps. Include the translate in the hover value —
    // e.g. `translateY(-50%) scale(1.15)` — rather than `scale(1.15)` alone.
    sectionToggleHover?: ISearchElementStyle;
    // The characters drawn when the section is collapsed / expanded. Default '+' and '−' (U+2212).
    // The title TEXT block inside a section's header band, styled independently of the band itself
    // (which is per-section titleStyleProps). Page-wide, so every section gets the same treatment
    // while keeping its own colour. Set `color` on the BAND, not here, or every section shares one.
    //
    // The knocked-out look: give the band `display:flex; alignItems:stretch` and give this
    // `{ backgroundColor:'#05060a', display:'flex', alignItems:'flex-end', padding:'0 0.6em' }` —
    // a black block the full height of the band, text sitting on its baseline, tinted by the band.
    sectionTitleText?: ISearchElementStyle;
    sectionToggleExpandIcon?: string;
    sectionToggleCollapseIcon?: string;
    // Hover styling for the home page's interactable tiles. Applied as scoped
    // `.selector:hover` rules with a short auto-transition, so any CSS passes through
    // (boxShadow, transform, borderColor, backgroundColor, …). `cardHover` = home product
    // cards; `podHover` = the category pods (the "category chips"); `navPodHover` = nav pods.
    cardHover?: ISearchElementStyle;
    podHover?: ISearchElementStyle;
    navPodHover?: ISearchElementStyle;

    // --- Full-bleed "bands" (set via raw JSON only) ---
    // The home page content sits in a max-width column; these let specific rows break out to the
    // full viewport width so a merchant can build edge-to-edge colored bands (à la a marketing
    // site) while the inner content still respects the storefront's maxWidth. Each is strictly
    // additive: unset = today's behavior (content stays in the column).

    // When true, the hero banner spans the full viewport width instead of the max-width column.
    // The overlay title/subtitle/button stay locked to the content width so they read as a hero.
    bannerFullWidth?: boolean;
    // When true (only meaningful with bannerFullWidth and a multi-banner carousel), the banner
    // box is sized to the image's NATURAL height (width:100%, height:auto) instead of the fixed
    // `bannerHeight` box with object-fit:cover. The banner then scales with viewport width and is
    // never cropped — on a wide/4K screen it grows taller rather than clipping the art top/bottom.
    // Only safe when the banners share one aspect ratio (otherwise slides jump height as they
    // rotate — which is exactly why the fixed-height box is the default). Single-banner full-bleed
    // already renders at natural height, so this only affects the carousel. Additive: unset = today.
    bannerAutoHeight?: boolean;
    // When true (full-bleed only), the banner's overlay text/CTA is sized and positioned in
    // container-query units so it scales WITH the banner — the button grows on wide/4K screens and
    // holds the same spot relative to the art, instead of locking to a fixed pixel maxWidth column.
    // Additive: unset = today's fixed page-frame placement.
    bannerOverlayScale?: boolean;
    // Seconds each banner is shown before the carousel advances. Unset = 7, the interval that has
    // always been hardcoded. Set this when a slide carries timed content the default cuts off — an
    // animated banner (GIF/animated WebP) whose play-once length exceeds 7s is the case this exists
    // for, since the carousel can't know how long the artwork runs.
    // `0` disables auto-advance entirely, leaving the dots as the only way to change slide.
    // Values are clamped to a sane floor (see BANNER_ROTATION_MIN_SECONDS) so a typo like 0.05
    // can't spin the carousel; only 2+ banners rotate at all.
    bannerRotationSeconds?: number;
    // How the hero banner image fits its (fixed-height) box. 'fit' = letterbox the whole image
    // (object-fit: contain); 'fill' = crop to fill the box (object-fit: cover). Unset falls back to
    // the bannerFullWidth heuristic (full-bleed → cover, otherwise contain).
    bannerImageFit?: 'fit' | 'fill';
    // Home page only, opt-in. When true AND bannerFullWidth is on, the transparent header floats
    // over the full-bleed hero image at the top of the page (the modern e-commerce look) instead of
    // sitting on its own solid bar above it. Desktop only (the mobile header stacks/columns and
    // can't float); the header turns solid once the shopper scrolls past the hero. Unset/false =
    // today's behavior (a solid header bar above the banner).
    bannerUnderHeader?: boolean;
    // Overlay-bar chrome for the not-yet-scrolled state of bannerUnderHeader mode (after scrolling
    // the bar reverts to its normal solid styling). Background is any CSS color — typically rgba
    // with alpha so the header text stays legible over a busy hero without hiding it. Shadow is a
    // CSS box-shadow softening the bar's edge. Unset = fully transparent / shadowless bar.
    bannerUnderHeaderBackground?: string;
    bannerUnderHeaderShadow?: string;
    // Full-width band painted behind the nav-pod row (e.g. { backgroundColor:'#F58220',
    // padding:'24px 0' }). The pods themselves stay centered within maxWidth.
    navPodBand?: ISearchElementStyle;
    // Per-section full-width bands, indexed by the section's render order (sectionBands[0] styles
    // the first section, [1] the second, …). A section with no entry renders un-banded. Use this to
    // stripe alternating colors (e.g. white / #E7ECF2). The section's own container (styleProps)
    // still paints inside the band, so drop its border / make it transparent to let the band show.
    sectionBands?: ISearchElementStyle[];

    // --- Uniform block spacing (opt-in; px) ---
    // These two knobs close the vertical gaps between the home page's stacked blocks so full-bleed
    // bands can stack flush (e.g. Lumen21's alternating-color-band look). Both strictly additive:
    // undefined leaves every margin/padding involved exactly as it is today.

    // Applied as marginTop on the outer home container — controls the header (NavBar) → banner gap
    // above the very first home block (the <h1 class="home-title">, or the banner if the title
    // renders empty). Positive values add clearance; negative values pull the block up into the
    // navbar's own clearance (e.g. to close the residual few px left by #content's padding-top).
    // When set, also collapses the title's own top/bottom margins so the banner sits flush beneath
    // it either way.
    homeContentPaddingTop?: number;
    // Uniform vertical gap between every stacked home block — banner, bannerText, nav pods
    // (band or not), category pods, the search bar, and the sections container. When set, this
    // replaces each of those blocks' individual margins/padding with one consistent gap (applied via
    // a flex column + rowGap on their shared wrapper); when unset, today's per-block spacing is
    // untouched.
    homeBlockGap?: number;

    // --- Full-width page layout (opt-in) ---
    // When true, #content-inner (the page content wrapper — Layout.tsx) spans the full
    // viewport width at all breakpoints instead of the default 90% column, so full-bleed
    // bands (banner/nav-pod band/section bands, which lay out at 100vw) can reach the
    // browser edges without being clipped by the 90% column. Strictly additive: unset/false
    // leaves #content-inner exactly as it is today (90% width via index.css).
    // Carousel dot placement. Absent/false keeps the shipped look: the dots overlay the bottom of
    // the hero image. True moves them into normal flow UNDERNEATH the banner, which is where the
    // admin editor's preview has always drawn them — so preview and storefront only agree on a store
    // that opts in. Only meaningful with 2+ banners (no dots render otherwise).
    bannerDotsBelow?: boolean;
    fullWidthLayout?: boolean;

    // --- Affiliations / endorsements / awards band (opt-in) ---
    // A row of logo images with an optional label, placed on the home page (default: bottom,
    // after the product sections). Strictly additive: unset, or set with no items carrying an
    // imageUrl, renders nothing — the home page is unchanged.
    affiliations?: IAffiliations;

    // --- Browse All CTA band (opt-in, AC-969) ---
    // When set, the bottom "Browse All" area becomes a full-bleed call-to-action band: an optional
    // heading + subheading above the (styled) Browse All button, over a color/image background (with
    // an optional wash + fixed-background parallax). Absent = the plain centered Browse All button.
    browseAllBand?: {
        heading?: string;
        subheading?: string;
        backgroundColor?: string;
        backgroundImageUrl?: string;
        overlayColor?: string;        // tint painted over the image (a wash)
        overlayOpacity?: number;      // 0-100; the wash's strength (default 50)
        fixed?: boolean;              // background-attachment: fixed (a fixed-background parallax)
        minHeight?: number;           // band height in px (default 320)
        headingStyle?: ISearchTextStyle;
        subheadingStyle?: ISearchTextStyle;
        buttonStyle?: ISearchElementStyle;
    };
}

export const DEFAULT_HOME_PAGE_CONFIG: IHomePageConfig = {};

export function parseHomePageConfig(raw?: string): IHomePageConfig {
    if (!raw) return { ...DEFAULT_HOME_PAGE_CONFIG };
    try {
        const parsed = JSON.parse(raw) as IHomePageConfig;
        return { ...DEFAULT_HOME_PAGE_CONFIG, ...parsed };
    } catch {
        return { ...DEFAULT_HOME_PAGE_CONFIG };
    }
}

// True when the storefront has at least one banner it can actually render: any storefrontBanners
// entry with a non-empty (trimmed) imageUrl, or the legacy single storefrontBannerImage. Shared by
// Layout and HomePage so the "hero under transparent header" activation test lives in one place.
//
// pageId scopes the check to a specific custom page (mirrors HomePage's validBanners filter): only
// banners tagged with that page id count, and the legacy single-image fallback is excluded (it's a
// main-homepage concept only). Omit pageId for the main homepage to keep today's behavior exactly.
export function hasRenderableBanner(
    settings?: Pick<IStorefrontSettings, 'storefrontBanners' | 'storefrontBannerImage'>,
    pageId?: string,
): boolean {
    if (!settings) return false;
    if (pageId) {
        return !!settings.storefrontBanners?.some(b => b.page === pageId && !!b.imageUrl && b.imageUrl.trim() !== '');
    }
    if (settings.storefrontBanners?.some(b => !!b.imageUrl && b.imageUrl.trim() !== '')) return true;
    return !!settings.storefrontBannerImage && settings.storefrontBannerImage.trim() !== '';
}

// The carousel's dwell time per slide. 7s was hardcoded before bannerRotationSeconds existed, so an
// unset (or unusable) value has to resolve to exactly that or every existing storefront changes pace.
export const BANNER_ROTATION_DEFAULT_SECONDS = 7;
// A carousel that flips faster than this is unreadable, and a mistyped 0.05 would thrash the render
// loop, so a positive value below the floor is raised to it rather than honored or thrown away.
export const BANNER_ROTATION_MIN_SECONDS = 2;

// Milliseconds between banner advances, or null when the merchant has turned auto-advance off
// (bannerRotationSeconds: 0) and the dots become the only way to change slide. Anything that isn't
// a finite number — a string from hand-edited JSON, NaN, Infinity — falls back to the default
// rather than disabling rotation, since a broken value shouldn't silently freeze the hero.
export function bannerRotationMs(config?: Pick<IHomePageConfig, 'bannerRotationSeconds'>): number | null {
    const seconds = config?.bannerRotationSeconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return BANNER_ROTATION_DEFAULT_SECONDS * 1000;
    if (seconds <= 0) return null;
    return Math.max(seconds, BANNER_ROTATION_MIN_SECONDS) * 1000;
}

// #content-inner supplies a 1em side gutter only in the BOXED layout above phone width. It is absent
// on phones (the edge-to-edge rule) and, for a fullWidthLayout store, at every width up to its
// maxWidth cap — above the cap the column centers and regains a natural margin. Wherever the column
// runs to the viewport edge, content flush at its left edge sits on the clipping boundary and the
// leftmost glyph can shave. This is the minimum inset applied in that case — small enough to preserve
// the edge-to-edge feel.
export const EDGE_MIN_LEFT_INSET = '8px';

// The left component of a CSS box shorthand ('0 0 8px' -> '0', '4px 8px' -> '8px'), following the
// 1/2/3/4-value rules. Returns undefined for an absent/empty value.
export const shorthandLeftValue = (value?: string | number): string | undefined => {
    if (value == null || value === '') return undefined;
    const parts = String(value).trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    if (parts.length < 4) return parts[1];
    return parts[3];
};

// True when a style leaves its left edge flush — no paddingLeft, and no left component in a padding
// shorthand (or one that resolves to zero). Used to decide whether a narrow-width guard inset is
// needed; a merchant who set their own left padding keeps it untouched.
export const hasZeroLeftInset = (style?: { padding?: string | number; paddingLeft?: string | number }): boolean => {
    const left = style?.paddingLeft ?? shorthandLeftValue(style?.padding);
    if (left == null || left === '') return true;
    const n = parseFloat(String(left));
    return !Number.isNaN(n) && n === 0;
};
